// src/utils/anonymousUser.js
// Utility for managing anonymous user identification

const STORAGE_KEY = 'rxrooster_anonymous_user_id';

/**
 * Get or create anonymous user ID
 * @returns {string} Anonymous user ID
 */
export const getAnonymousUserId = () => {
  // Check if ID already exists in localStorage
  let anonymousUserId = localStorage.getItem(STORAGE_KEY);
  
  if (!anonymousUserId) {
    // Generate new anonymous user ID
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 11);
    anonymousUserId = `anon_${timestamp}_${randomString}`;
    
    // Store in localStorage
    localStorage.setItem(STORAGE_KEY, anonymousUserId);
    
    console.log('Generated new anonymous user ID:', anonymousUserId);
  }
  
  return anonymousUserId;
};

/**
 * Check if user has an anonymous ID
 * @returns {boolean}
 */
export const hasAnonymousUserId = () => {
  return !!localStorage.getItem(STORAGE_KEY);
};

/**
 * Clear anonymous user ID (call after registration)
 */
export const clearAnonymousUserId = () => {
  const anonymousUserId = localStorage.getItem(STORAGE_KEY);
  if (anonymousUserId) {
    console.log('Clearing anonymous user ID:', anonymousUserId);
    localStorage.removeItem(STORAGE_KEY);
  }
};

/**
 * Get anonymous user ID without creating one
 * @returns {string|null}
 */
export const getExistingAnonymousUserId = () => {
  return localStorage.getItem(STORAGE_KEY);
};

const MARKETING_ANON_COOKIE = '_rxr_aid';

/**
 * Read the marketing site's cross-subdomain anonymous id cookie (`_rxr_aid`).
 * The marketing site (rxrooster.com) sets this on `.rxrooster.com`, so it is
 * readable on app.rxrooster.com in production. Read-only here: we never create
 * the cookie (the marketing site owns its lifecycle). Returns the decoded value
 * or undefined when absent or unavailable (SSR / no document.cookie).
 * @returns {string|undefined} Marketing anonymous id
 */
export const getMarketingAnonId = () => {
  try {
    if (typeof document === 'undefined' || !document.cookie) return undefined;
    const match = document.cookie
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith(`${MARKETING_ANON_COOKIE}=`));
    if (!match) return undefined;
    const value = match.slice(MARKETING_ANON_COOKIE.length + 1);
    return value ? decodeURIComponent(value) : undefined;
  } catch (e) {
    // document.cookie unavailable / malformed — best-effort, no-op
    return undefined;
  }
};

const SESSION_KEY = 'rxrooster_session_id';

/**
 * Tab-scoped session id (sessionStorage clears when the tab closes).
 * @returns {string} Session ID
 */
export const getSessionId = () => {
  let id = null;
  try {
    id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `sess_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
  } catch (e) {
    // sessionStorage unavailable (private mode edge cases) — best-effort
    id = 'sess_ephemeral';
  }
  return id;
};
